import { useId } from 'react'
import type { ChecklistAnswer, FormAnswer, FormAnswers, FormField, YesNoAnswer } from '../api/client'
import './forms.css'

interface Props {
  fields: FormField[]
  answers: FormAnswers
  onChange: (answers: FormAnswers) => void
  readOnly?: boolean
  // Messages from validateAnswers, keyed by field id.
  errors?: Record<string, string>
}

// A form on one scrolling page: each heading starts a group, and the fields
// before the first heading sit in a group of their own with no title. The
// same view fills in a form and shows a signed one (readOnly).
export default function FormView({ fields, answers, onChange, readOnly = false, errors = {} }: Props) {
  const base = useId()
  const set = (fieldId: string, answer: FormAnswer | undefined) => {
    const next = { ...answers }
    if (answer === undefined) delete next[fieldId]
    else next[fieldId] = answer
    onChange(next)
  }

  const groups: { heading: FormField | null; items: FormField[] }[] = []
  for (const f of fields) {
    if (f.type === 'heading') groups.push({ heading: f, items: [] })
    else {
      if (groups.length === 0) groups.push({ heading: null, items: [] })
      groups[groups.length - 1].items.push(f)
    }
  }

  return (
    <div className="form-view">
      {groups.map((g, i) => {
        const headingId = g.heading ? `${base}-${g.heading.id}` : undefined
        return (
          <section key={g.heading?.id ?? `top-${i}`} className="form-group" aria-labelledby={headingId}>
            {g.heading && (
              <h2 id={headingId} className="form-heading">
                {g.heading.label}
              </h2>
            )}
            {g.items.map((f) => (
              <Field
                key={f.id}
                field={f}
                domId={`${base}-${f.id}`}
                answer={answers[f.id]}
                error={errors[f.id]}
                readOnly={readOnly}
                onAnswer={(a) => set(f.id, a)}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}

interface FieldProps {
  field: FormField
  domId: string
  answer: FormAnswer | undefined
  error: string | undefined
  readOnly: boolean
  onAnswer: (answer: FormAnswer | undefined) => void
}

function Required({ field }: { field: FormField }) {
  if (!field.required && field.type !== 'statement') return null
  return (
    <>
      <span aria-hidden="true"> *</span>
      <span className="form-hidden"> (required)</span>
    </>
  )
}

function Field({ field, domId, answer, error, readOnly, onAnswer }: FieldProps) {
  const errorId = error ? `${domId}-error` : undefined
  const invalid = error ? true : undefined
  const message = error && (
    <p id={errorId} className="form-error" role="alert">
      {error}
    </p>
  )

  switch (field.type) {
    case 'text':
      return <p className="form-text">{field.label}</p>

    case 'short':
    case 'date':
      return (
        <div className="form-field">
          <label htmlFor={domId} className="form-label">
            {field.label}
            <Required field={field} />
          </label>
          <input
            id={domId}
            type={field.type === 'date' ? 'date' : 'text'}
            value={typeof answer === 'string' ? answer : ''}
            onChange={(e) => onAnswer(e.target.value === '' ? undefined : e.target.value)}
            readOnly={readOnly}
            aria-invalid={invalid}
            aria-describedby={errorId}
          />
          {message}
        </div>
      )

    case 'long':
      return (
        <div className="form-field">
          <label htmlFor={domId} className="form-label">
            {field.label}
            <Required field={field} />
          </label>
          <textarea
            id={domId}
            rows={4}
            value={typeof answer === 'string' ? answer : ''}
            onChange={(e) => onAnswer(e.target.value === '' ? undefined : e.target.value)}
            readOnly={readOnly}
            aria-invalid={invalid}
            aria-describedby={errorId}
          />
          {message}
        </div>
      )

    case 'yes_no': {
      const current: YesNoAnswer = typeof answer === 'object' && answer !== null && 'value' in answer ? answer : { value: null }
      const choose = (value: boolean) => onAnswer({ value, ...(value && current.details ? { details: current.details } : {}) })
      return (
        <fieldset className="form-field" aria-describedby={errorId}>
          <legend className="form-label">
            {field.label}
            <Required field={field} />
          </legend>
          <div className="form-choices form-choices-row">
            {([true, false] as const).map((v) => (
              <label key={String(v)} className="form-choice">
                <input
                  type="radio"
                  name={domId}
                  checked={current.value === v}
                  onChange={() => choose(v)}
                  disabled={readOnly}
                />
                <span>{v ? 'Yes' : 'No'}</span>
              </label>
            ))}
          </div>
          {field.details && current.value === true && (
            <div className="form-details">
              <label htmlFor={`${domId}-details`} className="form-sublabel">
                Details
              </label>
              <textarea
                id={`${domId}-details`}
                rows={2}
                value={current.details ?? ''}
                onChange={(e) => onAnswer({ value: true, ...(e.target.value ? { details: e.target.value } : {}) })}
                readOnly={readOnly}
              />
            </div>
          )}
          {message}
        </fieldset>
      )
    }

    case 'checklist': {
      const current: ChecklistAnswer =
        typeof answer === 'object' && answer !== null && 'ticked' in answer ? answer : { ticked: [], none: false }
      const emit = (next: ChecklistAnswer) => onAnswer(next.ticked.length === 0 && !next.none ? undefined : next)
      return (
        <fieldset className="form-field" aria-describedby={errorId}>
          <legend className="form-label">
            {field.label}
            <Required field={field} />
          </legend>
          <div className="form-choices">
            {field.options.map((o) => (
              <label key={o.id} className="form-choice">
                <input
                  type="checkbox"
                  checked={current.ticked.includes(o.id)}
                  onChange={(e) =>
                    emit({
                      ticked: e.target.checked ? [...current.ticked, o.id] : current.ticked.filter((id) => id !== o.id),
                      none: false,
                    })
                  }
                  disabled={readOnly}
                />
                <span>{o.label}</span>
              </label>
            ))}
            {field.none_option && (
              <label className="form-choice">
                <input
                  type="checkbox"
                  checked={current.none}
                  onChange={(e) => emit({ ticked: [], none: e.target.checked })}
                  disabled={readOnly}
                />
                <span>None of these</span>
              </label>
            )}
          </div>
          {message}
        </fieldset>
      )
    }

    case 'choice':
      return (
        <fieldset className="form-field" aria-describedby={errorId}>
          <legend className="form-label">
            {field.label}
            <Required field={field} />
          </legend>
          <div className="form-choices">
            {field.options.map((o) => (
              <label key={o.id} className="form-choice">
                <input
                  type="radio"
                  name={domId}
                  checked={answer === o.id}
                  onChange={() => onAnswer(o.id)}
                  disabled={readOnly}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {message}
        </fieldset>
      )

    case 'statement':
      return (
        <div className="form-field">
          <label className="form-choice form-statement">
            <input
              type="checkbox"
              checked={answer === true}
              onChange={(e) => onAnswer(e.target.checked ? true : undefined)}
              disabled={readOnly}
              aria-invalid={invalid}
              aria-describedby={errorId}
            />
            <span>
              {field.label}
              <Required field={field} />
            </span>
          </label>
          {message}
        </div>
      )
  }
  return null
}
